/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/require-param-description */
import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import moment from "moment";
import holdIdImg from "../../../../../img/holdId.png";
import idcloseup from "../../../../../img/holdid2.png";
import FileUploader from "../FileUploader";

const useStyles = makeStyles(theme => ({
  formContainer: {
    marginTop: theme.spacing(2),
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    flexGrow: 1,
  },
  exampleImage: {
    width: "100%",
    maxWidth: 250,
    height: "auto",
    margin: "0 auto",
    display: "block",
    marginTop: theme.spacing(2),
  },
  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(3),
  },
  uploadSection: {
    textAlign: "center",
    padding: theme.spacing(3),
  },
  uploadedImage: {
    width: "100%",
    maxHeight: 200,
    objectFit: "contain",
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  uploadStatus: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  removeButton: {
    marginTop: theme.spacing(1),
  },
}));

/**
 * DocumentsForm component - simplified document upload
 * @param root0
 * @param root0.formData
 * @param root0.onSave
 * @param root0.onBack
 * @param root0.onNext
 * @param root0.submitLabel
 */
export default function DocumentsForm({
  formData,
  onSave,
  onBack,
  onNext,
  submitLabel = "Save & Continue",
}) {
  const classes = useStyles();

  // Local state for form values
  const [values, setValues] = useState({
    idPhotoUrl: formData.idPhotoUrl || "",
    personPhotoUrl: formData.personPhotoUrl || "",
  });

  // eslint-disable-next-line no-unused-vars
  const [uploading, setUploading] = useState({
    idPhoto: false,
    personPhoto: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setValues({
      idPhotoUrl: formData.idPhotoUrl || "",
      personPhotoUrl: formData.personPhotoUrl || "",
    });
  }, [formData]);

  // Handle ID photo upload
  const handleIdPhotoUpload = file => {
    if (file && file.url) {
      const newValues = { ...values, idPhotoUrl: file.url };
      
      setValues(newValues);

      // Auto-save to parent immediately
      if (onSave) {
        const dataToSave = {
          ...formData,
          ...newValues,
        };
        onSave(dataToSave);
      }

      // Clear error
      if (errors.idPhotoUrl) {
        setErrors(prev => ({ ...prev, idPhotoUrl: "" }));
      }
    }
  };

  const handlePersonPhotoUpload = file => {
    if (file && file.url) {
      const newValues = { ...values, personPhotoUrl: file.url };
      
      setValues(newValues);

      // Auto-save to parent immediately
      if (onSave) {
        const dataToSave = {
          ...formData,
          ...newValues,
        };
        onSave(dataToSave);
      }

      // Clear error
      if (errors.personPhotoUrl) {
        setErrors(prev => ({ ...prev, personPhotoUrl: "" }));
      }
    }
  };

  // Remove ID photo
  const handleRemoveIdPhoto = () => {
    const newValues = { ...values, idPhotoUrl: "" };
    
    setValues(newValues);

    // Auto-save to parent immediately
    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }
  };

  // Remove person photo
  const handleRemovePersonPhoto = () => {
    const newValues = { ...values, personPhotoUrl: "" };
    
    setValues(newValues);

    // Auto-save to parent immediately
    if (onSave) {
      const dataToSave = {
        ...formData,
        ...newValues,
      };
      onSave(dataToSave);
    }
  };

  // Basic validation
  const validateForm = () => {
    const newErrors = {};

    if (!values.idPhotoUrl) {
      newErrors.idPhotoUrl = "ID photo is required";
    }

    if (!values.personPhotoUrl) {
      newErrors.personPhotoUrl = "Person photo is required";
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Basic validation
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Save data first
    if (onSave) {
      onSave(values);
    }

    // Move to next step
    if (onNext) {
      onNext(values);
    }
  };

  return (
    <form className={classes.formContainer} onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Document Upload
      </Typography>

      <Typography color="textSecondary" paragraph>
        Please upload clear photos of the required documents. Make sure the
        documents are clearly visible and readable.
      </Typography>

      <Grid container spacing={3}>
        {/* ID Photo Upload */}
        <Grid item xs={12} md={8}>
          <Card className={classes.card} variant="outlined">
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom variant="h6">
                ID/Passport Photo *
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Upload a clear photo of the user's ID document or passport
              </Typography>

              {values.idPhotoUrl ? (
                <Box className={classes.uploadSection}>
                  <img
                    src={values.idPhotoUrl}
                    alt="ID document"
                    className={classes.uploadedImage}
                  />
                  <Box className={classes.uploadStatus}>
                    <CheckCircleIcon color="primary" />
                    <Typography color="primary">
                      ID photo uploaded successfully
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleRemoveIdPhoto}
                    className={classes.removeButton}
                  >
                    Remove Photo
                  </Button>
                </Box>
              ) : (
                <Box className={classes.uploadSection}>
                  <CloudUploadIcon style={{ fontSize: 48, color: "#ccc" }} />
                  <FileUploader
                    sendFile={handleIdPhotoUpload}
                    label="Choose ID/Passport Photo"
                    storagePath={`users/documents/id/${moment().format("X")}`} // Unique Path
                  />
                  {errors.idPhotoUrl && (
                    <Typography color="error" variant="caption" display="block">
                      {errors.idPhotoUrl}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ID Photo Example */}
        <Grid item xs={12} md={4}>
          <Card className={classes.card} variant="outlined">
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom variant="h6">
                Example
              </Typography>
              <Typography color="textSecondary" variant="body2">
                The ID should be clearly visible and readable
              </Typography>
              <img
                src={idcloseup}
                alt="ID example"
                className={classes.exampleImage}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Person Photo Upload */}
        <Grid item xs={12} md={8}>
          <Card className={classes.card} variant="outlined">
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom variant="h6">
                Person Holding ID *
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Upload a clear photo of the person holding their ID document
              </Typography>

              {values.personPhotoUrl ? (
                <Box className={classes.uploadSection}>
                  <img
                    src={values.personPhotoUrl}
                    alt="Person holding ID"
                    className={classes.uploadedImage}
                  />
                  <Box className={classes.uploadStatus}>
                    <CheckCircleIcon color="primary" />
                    <Typography color="primary">
                      Person photo uploaded successfully
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleRemovePersonPhoto}
                    className={classes.removeButton}
                  >
                    Remove Photo
                  </Button>
                </Box>
              ) : (
                <Box className={classes.uploadSection}>
                  <CloudUploadIcon style={{ fontSize: 48, color: "#ccc" }} />
                  <FileUploader
                    sendFile={handlePersonPhotoUpload}
                    label="Choose Person Photo"
                    storagePath={`users/documents/person/${moment().format(
                      "X"
                    )}`} // Unique Path
                  />
                  {errors.personPhotoUrl && (
                    <Typography color="error" variant="caption" display="block">
                      {errors.personPhotoUrl}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Person Photo Example */}
        <Grid item xs={12} md={4}>
          <Card className={classes.card} variant="outlined">
            <CardContent className={classes.cardContent}>
              <Typography gutterBottom variant="h6">
                Example
              </Typography>
              <Typography color="textSecondary" variant="body2">
                Both face and ID should be clearly visible
              </Typography>
              <img
                src={holdIdImg}
                alt="Person holding ID example"
                className={classes.exampleImage}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Upload Instructions */}
        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Important:</strong> Please ensure that:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Photos are clear and not blurry</li>
              <li>All text on the ID is readable</li>
              <li>The entire ID document is visible</li>
              <li>
                For the person photo, both face and ID are clearly visible
              </li>
              <li>Photos are taken in good lighting</li>
            </ul>
          </Alert>
        </Grid>
      </Grid>

      <div className={classes.buttons}>
        <Button onClick={onBack} disabled={!onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={uploading.idPhoto || uploading.personPhoto}
        >
          {uploading.idPhoto || uploading.personPhoto ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              Uploading...
            </Box>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}
